import { parse, subDays } from 'date-fns';

import type { PostingFilters, PostingSourceId } from '@revelio/data-access';
import type { SelectionList, ValidValueTypes } from '@revelio/iso-utility';

import { DEFAULT_SKILL_TAXONOMY_ID } from '../hooks/useAdaptiveSkillTaxonomy';
import {
  type Filter,
  FilterId,
  FilterType,
  type PrimaryFilterId,
} from '../types/filters';
import { getFormattedDateString, localToUTC } from './dateUtils';
import { getCustomRoleFilter } from './getCustomRoleFilter';
import { getCustomSkillFilter } from './getCustomSkillFilter';
import { clampDates, getDateFromFilters } from './getDateFilter';
import { getDim1 } from './getDim1';
import { getListFilter } from './getListFilter';
import { getV2CompanyFilters } from './getV2CompanyFilters';
import { getV2GeographyFilters } from './getV2GeographyFilters';
import {
  homogenizeRicsSelections,
  homogenizeRoleSelections,
} from './homogenize-tree-selections';
import { getProviderMinStartDate } from './providerDateOverrides';
import { stripVersionFromTaxonomyId } from './taxonomyHelpers';

/**
 * Preprocess filters to homogenize ROLE and COMPANY (RICS) selections.
 * Returns a new filter array with homogenized TreeFilter selections.
 */
const homogenizeFilters = (
  filters: Filter[],
  selectionListsArr: SelectionList<ValidValueTypes>[]
): Filter[] =>
  filters.map((filter) => {
    if (filter.type !== FilterType.TREE) return filter;

    if (filter.id === FilterId.ROLE) {
      return {
        ...filter,
        selections: homogenizeRoleSelections(filter, selectionListsArr),
      };
    }

    if (filter.id === FilterId.COMPANY) {
      return {
        ...filter,
        selections: homogenizeRicsSelections(filter, selectionListsArr),
      };
    }

    return filter;
  });

const PostingDateGranularity = 'week' as const;

export const getPostingFilters = (
  primaryDimension: PrimaryFilterId,
  effectiveFilters: Filter[],
  options?: {
    isSnapshot?: boolean;
    /**
     * When true, homogenize ROLE and COMPANY (RICS) filters to a single granularity level.
     * Required for APIs that don't support mixed granularity levels.
     * Requires selectionListsForHomogenization to be provided.
     */
    homogenize?: boolean;
    /**
     * Selection lists array (from useAllSelectionLists) required for homogenization.
     * Only used when homogenize is true.
     */
    selectionListsForHomogenization?: SelectionList<ValidValueTypes>[];
    defaultMetricMode?: 'posting' | 'expected_hires';
  }
): PostingFilters => {
  let hasUserSubmittedDateFilter = false;
  const isSnapshot = options?.isSnapshot ?? false;
  const homogenize = options?.homogenize ?? false;
  const selectionListsForHomogenization =
    options?.selectionListsForHomogenization;

  const filtersToEncode =
    homogenize && selectionListsForHomogenization
      ? homogenizeFilters(effectiveFilters, selectionListsForHomogenization)
      : effectiveFilters;

  const baseFilters = filtersToEncode.reduce<PostingFilters>(
    (acc, filter): PostingFilters => {
      const filterId = filter.id;

      return ((): PostingFilters => {
        switch (filterId) {
          case FilterId.COMPANY:
            return { ...acc, ...getV2CompanyFilters(filter) };

          case FilterId.GEOGRAPHY:
            return { ...acc, ...getV2GeographyFilters(filter) };

          case FilterId.ROLE:
            return { ...acc, ...getCustomRoleFilter(filter, effectiveFilters) };

          case FilterId.CUSTOM_SKILL:
            return {
              ...acc,
              ...getCustomSkillFilter(filter, effectiveFilters),
            };

          case FilterId.SKILL_TAXONOMY: {
            if (filter.type === FilterType.VALUE && filter.value) {
              const taxonomyId =
                (filter.value as { taxonomyId?: number })?.taxonomyId ??
                DEFAULT_SKILL_TAXONOMY_ID;
              return {
                ...acc,
                custom_skill: {
                  ...(acc.custom_skill || {}),
                  taxonomy_id: taxonomyId,
                  from_new_refs: true,
                },
              } as PostingFilters;
            }
            return acc;
          }

          case FilterId.SENIORITY:
            return {
              ...acc,
              ...getListFilter<PostingFilters>(filter, 'seniority'),
            };

          case FilterId.ROLE_TAXONOMY: {
            if (filter.type === FilterType.VALUE && filter.value) {
              const customRoleAcc = acc.custom_role || {};
              return {
                ...acc,
                custom_role: {
                  ...customRoleAcc,
                  taxonomyId: stripVersionFromTaxonomyId(filter.value),
                },
              };
            }
            return acc;
          }

          case FilterId.TITLE: {
            if (filter.type === FilterType.FREE_TEXT && 'text' in filter) {
              return { ...acc, title_raw: filter.text };
            }
            return acc;
          }

          case FilterId.POSTINGS_SOURCE: {
            if (
              filter.type === FilterType.STATIC_LIST &&
              'selections' in filter &&
              Array.isArray(filter.selections)
            ) {
              return {
                ...acc,
                provider: filter.selections.map((selection) =>
                  // biome-ignore lint/correctness/useParseIntRadix: migrated from develop
                  parseInt(selection.id)
                ),
              };
            }
            return acc;
          }

          case FilterId.METRIC_MODE: {
            if (
              filter.type === FilterType.STATIC_LIST &&
              'selections' in filter &&
              Array.isArray(filter.selections) &&
              filter.selections.length === 1
            ) {
              const metricMode = !filter.isUserSubmitted
                ? options?.defaultMetricMode
                : filter.selections?.[0]?.id;
              return {
                ...acc,
                metric_mode: metricMode ?? 'expected_hires',
              };
            }
            return acc;
          }

          // TODO: re-enable when this is supported by postings
          // case FilterId.KEYWORDS: {
          //   if (filter.type === FilterType.FREE_TEXT && 'text' in filter) {
          //     return { ...acc, keywords: filter.text };
          //   }
          //   return acc;
          // }
          case FilterId.DATE_RANGE:
          case FilterId.SNAPSHOT_DATE: {
            if (filter.isUserSubmitted) hasUserSubmittedDateFilter = true;
            return acc;
          }
          case FilterId.KEYWORDS:
          case FilterId.OVERALL_RATING:
          case FilterId.DATA_ADJUSTMENTS:
          case FilterId.ETHNICITY:
          case FilterId.EDUCATION:
          case FilterId.GENDER:
          case FilterId.CONTINGENT_WORKERS:
            return acc;
          default:
            return acc;
        }
      })();
    },
    {}
  );

  const dateFilterBase = getDateFromFilters({
    filters: filtersToEncode,
    granularity: PostingDateGranularity,
    isSnapshot,
  });
  const dateFilter =
    isSnapshot &&
    dateFilterBase?.start_date &&
    dateFilterBase.end_date &&
    // Postings snapshot requests need a non-zero range
    dateFilterBase.start_date === dateFilterBase.end_date
      ? {
          ...dateFilterBase,
          start_date: getFormattedDateString(
            localToUTC(
              subDays(
                parse(dateFilterBase.end_date, 'yyyy-MM-dd', new Date()),
                14
              )
            ),
            PostingDateGranularity
          ),
        }
      : dateFilterBase;

  const minPostingStartDate = !hasUserSubmittedDateFilter
    ? getProviderMinStartDate({
        providerIds: (baseFilters.provider as PostingSourceId[]) ?? [],
        dim1: getDim1(primaryDimension),
        filters: filtersToEncode,
      })
    : null;

  return {
    ...baseFilters,
    ...(minPostingStartDate
      ? clampDates({
          start_date: dateFilter?.start_date,
          end_date: dateFilter?.end_date,
          minStartDate: parse(minPostingStartDate, 'yyyy-MM-dd', new Date()),
          dateGranularity: PostingDateGranularity,
        })
      : dateFilter),
  };
};
